/**
 * Variant Image Filter for Dawn Theme
 * ------------------------------------
 * When a Color variant is selected → show only that variant's images.
 * When deselected / no color → show all images.
 *
 * Drop this file into your theme's assets/ folder and include it in
 * main-product.liquid (already done if you use the updated liquid file).
 */

(function () {
  'use strict';

  class VariantImageFilter {
    constructor(sectionId) {
      this.sectionId   = sectionId;
      this.section     = document.querySelector(`#MainProduct-${sectionId}`);
      if (!this.section) return;

      this.gallery     = this.section.querySelector('.product__media-list, [id^="Slider-Gallery"]');
      this.mediaItems  = null; // populated on first variant change

      this.bindEvents();
    }

    bindEvents() {
      // Dawn dispatches this custom event whenever a variant changes
      this.section.addEventListener('variantChange', (e) => {
        this.onVariantChange(e.detail?.variant);
      });

      // Also listen on document for themes that bubble it up
      document.addEventListener('variant:change', (e) => {
        if (e.detail?.sectionId === this.sectionId) {
          this.onVariantChange(e.detail?.variant);
        }
      });

      // Hook into Dawn's native variant selector change
      const variantSelects = this.section.querySelectorAll(
        'variant-selects, variant-radios'
      );
      variantSelects.forEach((el) => {
        el.addEventListener('change', () => {
          // Small delay so Dawn's own JS updates first
          setTimeout(() => this.readCurrentVariant(), 50);
        });
      });

      // Also watch the hidden variant input that Dawn updates
      const hiddenInput = this.section.querySelector('input[name="id"]');
      if (hiddenInput) {
        const observer = new MutationObserver(() => this.readCurrentVariant());
        observer.observe(hiddenInput, { attributes: true, attributeFilter: ['value'] });
      }
    }

    readCurrentVariant() {
      const input    = this.section.querySelector('input[name="id"]');
      const variantId = input?.value;
      if (!variantId) return;

      const variantData = this.getVariantData();
      const variant     = variantData?.find((v) => String(v.id) === String(variantId));
      this.onVariantChange(variant);
    }

    getVariantData() {
      if (this._variantData) return this._variantData;
      const script = this.section.querySelector('[type="application/json"][data-variant-json], .product-json');
      if (script) {
        try { this._variantData = JSON.parse(script.textContent); } catch (e) {}
      }
      // Dawn stores it in a separate element
      if (!this._variantData) {
        const el = document.querySelector(`#ProductJSON-${this.sectionId}`);
        if (!el) return null;
        try { this._variantData = JSON.parse(el.textContent); } catch (e) {}
      }
      return this._variantData;
    }

    onVariantChange(variant) {
      if (!variant) { this.showAllMedia(); return; }

      // Collect all media list items
      const allItems = this.section.querySelectorAll(
        '.product__media-item, .slider__slide.product__media-item'
      );
      if (!allItems.length) { return; }

      // Get the featured_media id for this variant
      const featuredMediaId = variant.featured_media?.id;

      if (!featuredMediaId) {
        // Variant has no specific image → show everything
        this.showAllMedia();
        return;
      }

      // Get ALL media ids that belong to this variant (Dawn stores them on data attrs)
      // We look at which items have data-media-id matching variant's media
      const variantMediaIds = this.getVariantMediaIds(variant);

      if (!variantMediaIds || variantMediaIds.length === 0) {
        this.showAllMedia();
        return;
      }

      // Show/hide media items
      let firstMatch = null;
      allItems.forEach((item) => {
        const mediaId = item.dataset.mediaId || item.querySelector('[data-media-id]')?.dataset.mediaId;
        const matches = variantMediaIds.includes(String(mediaId));

        if (matches) {
          item.style.display = '';
          item.removeAttribute('hidden');
          if (!firstMatch) firstMatch = item;
        } else {
          item.style.display = 'none';
          item.setAttribute('hidden', '');
        }
      });

      // Scroll/snap to first matching image
      if (firstMatch) {
        firstMatch.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
        // Also trigger Dawn's built-in gallery navigation if available
        const mediaId = firstMatch.dataset.mediaId || firstMatch.querySelector('[data-media-id]')?.dataset.mediaId;
        if (mediaId) {
          const galleryEl = this.section.querySelector('media-gallery');
          if (galleryEl && typeof galleryEl.setActiveMedia === 'function') {
            galleryEl.setActiveMedia(`${this.sectionId}-${mediaId}`, true);
          }
        }
      }
    }

    getVariantMediaIds(variant) {
      // Dawn's product JSON doesn't directly list all media per variant.
      // We use the featured_media.id as the primary, plus we scan all
      // variant-attached images from the gallery data attribute.
      const ids = [];

      if (variant.featured_media?.id) {
        ids.push(String(variant.featured_media.id));
      }

      // Also collect from data-variant-images attribute if present
      const galleryEl = this.section.querySelector('[data-variant-images]');
      if (galleryEl) {
        try {
          const map = JSON.parse(galleryEl.dataset.variantImages);
          const extra = map[variant.id] || [];
          extra.forEach((id) => ids.push(String(id)));
        } catch (e) {}
      }

      return [...new Set(ids)];
    }

    showAllMedia() {
      const allItems = this.section.querySelectorAll(
        '.product__media-item, .slider__slide.product__media-item'
      );
      allItems.forEach((item) => {
        item.style.display = '';
        item.removeAttribute('hidden');
      });
    }
  }

  // Auto-init for all product sections on the page
  function init() {
    document.querySelectorAll('[id^="MainProduct-"]').forEach((el) => {
      const sectionId = el.id.replace('MainProduct-', '');
      new VariantImageFilter(sectionId);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();